import type { FC } from 'react';

import { useFormatter } from '@eneco/i18n';
import { InputText, InputNumber, Heading, Text, Grid, InputDatepicker } from '@eneco/sparky';
import { UseFormRegister, FieldErrors, Controller, Control } from 'react-hook-form';

import type { FormValues } from './types';
import { hintOf } from '../contact-form/hint';
import type { DeceasedFormContent } from '../deceased-form-content.types';

type Props = {
  register: UseFormRegister<FormValues>;
  control: Control<FormValues, string>;
  content: DeceasedFormContent;
  errors: FieldErrors<FormValues>;
};

/**
 * Kept as the live site renders it: the section's `description` and `footerDescription` stay empty. The
 * legacy hands them to `@sitecore-jss`'s `RichText` as `html`, a prop that component ignores (it reads
 * `field`), so neither text has ever shown. The empty `Text` slots keep the layout identical.
 */
export const DeceasedInputs: FC<Props> = ({ register, content, control, errors }) => {
  const {
    deceasedFormFields_title: title,
    customerIdFormField,
    deceasedDateFormField,
    endContractDateFormField,
    deceasedFormFields_houseNumberFormField: houseNumberFormField,
    deceasedFormFields_houseNumberSuffixFormField: houseNumberSuffixFormField,
    deceasedFormFields_postalCodeFormField: postalCodeFormField,
  } = content;
  const { date } = useFormatter();

  return (
    <>
      <Heading as="h3" size="S">
        {title}
      </Heading>
      {/* Empty, as on the live site: see the note on `DeceasedInputs`. */}
      <Text size={{ initial: 'BodyM', md: 'BodyL' }} />
      <Grid columns={{ initial: '1', lg: '2' }} gap="6">
        <InputText
          label={postalCodeFormField?.label ?? ''}
          error={errors?.deceasedPostalCode?.message}
          hint={hintOf(postalCodeFormField?.hint)}
          placeholder={postalCodeFormField?.placeholder ?? ''}
          {...register('deceasedPostalCode')}
        />
        <InputText
          label={houseNumberFormField?.label ?? ''}
          error={errors?.deceasedHouseNumber?.message}
          hint={hintOf(houseNumberFormField?.hint)}
          placeholder={houseNumberFormField?.placeholder ?? ''}
          {...register('deceasedHouseNumber')}
        />
        <InputText
          label={houseNumberSuffixFormField?.label ?? ''}
          error={errors?.deceasedHouseNumberSuffix?.message}
          hint={hintOf(houseNumberSuffixFormField?.hint)}
          placeholder={houseNumberSuffixFormField?.placeholder ?? ''}
          {...register('deceasedHouseNumberSuffix')}
          isOptional
        />
        <InputNumber
          label={customerIdFormField?.label ?? ''}
          error={errors?.customerId?.message}
          {...register('customerId')}
          formatOptions={{ useGrouping: false }}
        />
        <Controller
          control={control}
          name="deceasedDate"
          render={({ field: { onChange, value, name } }) => (
            <InputDatepicker
              name={name}
              isReadOnly
              label={deceasedDateFormField?.label ?? ''}
              value={value}
              onDaySelect={d => onChange(d && date.short(d as Date))}
              fromYear={2000}
              toDate={new Date()}
              error={errors.deceasedDate?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="endContractDate"
          render={({ field: { onChange, value, name } }) => (
            <InputDatepicker
              name={name}
              isReadOnly
              label={endContractDateFormField?.label ?? ''}
              value={value}
              onDaySelect={d => onChange(d && date.short(d as Date))}
              fromYear={2000}
              error={errors.endContractDate?.message}
            />
          )}
        />
      </Grid>
      <Text size={{ initial: 'BodyM', md: 'BodyL' }} />
    </>
  );
};
